import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import LockPersonRounded from '@mui/icons-material/LockPersonRounded';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  EmptyState,
  RavenButton,
  RavenCard,
  RavenChip,
} from '@ravenappdev/raven-ui';
import { useAuth } from '../contexts/AuthContext';
import LoginPage from '../pages/LoginPage';
import DashboardPage from '../pages/DashboardPage';
import IsolationPlanningPage from '../pages/IsolationPlanningPage';
import AdminDashboardPage from '../pages/AdminDashboardPage';
import AdminFormSchemaPage from '../pages/AdminFormSchemaPage';
import PolicyAdminPage from '../pages/PolicyAdminPage';
import PipelineInspectionPage from '../pages/PipelineInspectionPage';
import App from '../App';
import PnidWorkspaceShell from '../components/product/PnidWorkspaceShell';
import PnidToolTheme from '../components/product/PnidToolTheme';
import '../packages/packages.css';

const PackageListPage = React.lazy(() => import('../packages/PackageListPage'));
const PackageDetailPage = React.lazy(() => import('../packages/PackageDetailPage'));
const AddDrawingsPage = React.lazy(() => import('../packages/AddDrawingsPage'));
const FileManagerPage = React.lazy(() => import('../pnid/FileManagerPage'));
const DrawingsLibraryPage = React.lazy(() => import('../drawings/DrawingsLibraryPage'));
const PlantHierarchyPage = React.lazy(() => import('../plant/PlantHierarchyPage'));
const LegendSheetsPage = React.lazy(() => import('../plant/LegendSheetsPage'));
const LegendSheetRecordPage = React.lazy(() => import('../plant/LegendSheetRecordPage'));
const NewLegendSheetPage = React.lazy(() => import('../plant/NewLegendSheetPage'));
const WorkspaceLandingPage = React.lazy(() => import('../workspace/WorkspaceLandingPage'));
const WorkspaceRunPage = React.lazy(() => import('../workspace/WorkspaceRunPage'));

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  fontFamily: 'system-ui',
};

function LoadingScreen() {
  return <div style={loadingStyle}>Loading...</div>;
}

function PrivateRoute({
  children,
  requireVerified = false,
}: {
  children: React.ReactNode;
  /** Wait for `/auth/me` when the page has no permission check of its own. */
  requireVerified?: boolean;
}) {
  const { isAuthenticated, isLoading, isVerifying } = useAuth();
  if (isLoading || (requireVerified && isVerifying)) return <LoadingScreen />;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function LoginRoute() {
  const { isAuthenticated, isLoading, isVerifying } = useAuth();
  if (isLoading || isVerifying) return <LoadingScreen />;
  return isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />;
}

function DashboardRoute() {
  const { isAuthenticated, isLoading, isVerifying, user } = useAuth();
  if (isLoading || isVerifying) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const userGroup = user?.user_group ?? '';
  const canViewDashboard = user?.username === 'raven_admin' || userGroup.endsWith('-reviewer');
  if (!canViewDashboard) return <Navigate to="/" replace />;

  return <DashboardPage />;
}

function AdminRoute() {
  const { isAuthenticated, isLoading, isVerifying, user } = useAuth();
  if (isLoading || isVerifying) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user?.permissions?.can_admin) return <Navigate to="/" replace />;
  return <AdminDashboardPage />;
}

function DrawingsRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isVerifying } = useAuth();
  if (isLoading || isVerifying) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  /* No package gate. This bounced accounts without can_manage_packages to the
     legacy picker at /, and that page is gone — with / now redirecting here,
     the pair would ping-pong forever. The library reads documents and
     annotations only, never a package endpoint, so it is exactly what those
     accounts used to get, better presented. The features that DO need
     permissions gate themselves: assignment on can_edit, Ask Raven on the
     workspace flag, and the rail's package destinations on their own checks. */
  return <React.Suspense fallback={<LoadingScreen />}>{children}</React.Suspense>;
}

/* Plant assets is open to every account that has a document scope, vendors
   included. Publishing from that page is gated on its own, on the backend and
   on the button, so opening the page grants no publish rights. */
function PlantAssetsRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isVerifying, user } = useAuth();
  if (isLoading || isVerifying) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  /* Every plant read is scoped by the caller's group, and the backend answers
     403 without one, so an account with no group goes to the library. */
  if (!user?.user_group?.trim()) return <Navigate to="/drawings" replace />;
  return <React.Suspense fallback={<LoadingScreen />}>{children}</React.Suspense>;
}


function PackageRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isVerifying, user, logout } = useAuth();
  if (isLoading || isVerifying) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const userGroup = user?.user_group?.trim() ?? '';
  const missingPackagePermission = !user?.permissions?.can_manage_packages;
  if (missingPackagePermission || !userGroup) {
    return (
      <PnidToolTheme>
        <main className="sme-access-shell">
          <div className="sme-access-card">
            <RavenCard variant="outlined">
              <EmptyState
                icon={<LockPersonRounded />}
                title="Package access is not enabled"
                description={`You are signed in as ${user?.username ?? 'this account'}, but this account cannot open the package workspace yet.`}
              />
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                  <RavenChip
                    label={`SME group · ${userGroup || 'Not assigned'}`}
                    colorVariant={userGroup ? 'success' : 'error'}
                    chipVariant="outlined"
                    size="small"
                  />
                  <RavenChip
                    label={`Manage packages · ${missingPackagePermission ? 'Not enabled' : 'Enabled'}`}
                    colorVariant={missingPackagePermission ? 'error' : 'success'}
                    chipVariant="outlined"
                    size="small"
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" align="center">
                  Sign in with the SME Demo account, or ask a Raven admin to assign an SME group and
                  enable <strong>Manage packages</strong> for that group.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
                  <RavenButton variant="contained" onClick={logout}>
                    Sign in with another account
                  </RavenButton>
                  <RavenButton variant="outlined" onClick={() => window.location.assign('/')}>
                    Return to drawings
                  </RavenButton>
                </Stack>
              </Stack>
            </RavenCard>
          </div>
        </main>
      </PnidToolTheme>
    );
  }
  return <React.Suspense fallback={<LoadingScreen />}>{children}</React.Suspense>;
}

/* Signing in lands here, and every account goes to the drawings library. This
   used to branch: accounts with package access were sent on, everyone else got
   the legacy picker rendered in place, outside the workspace shell and so
   without the navigation rail. The picker is gone, and with it the only reason
   / rendered anything of its own. Verification still has to settle first — the
   cached user restored from storage can carry permissions the account no longer
   has, and redirecting on a stale read would send someone to the wrong home. */
function HomeRoute() {
  const { isAuthenticated, isLoading, isVerifying } = useAuth();
  if (isLoading || isVerifying) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to="/drawings" replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      {/* Every signed-in surface renders inside the workspace shell so the
          navigation rail is reachable from anywhere the rail links to. The
          one exception is the legacy picker at / — it serves accounts the
          drawings library turns away, whose rail would only loop them home. */}
      <Route path="/dashboard" element={<PrivateRoute><PnidWorkspaceShell><DashboardRoute /></PnidWorkspaceShell></PrivateRoute>} />
      <Route path="/isolation-planning" element={<PrivateRoute><PnidWorkspaceShell><IsolationPlanningPage /></PnidWorkspaceShell></PrivateRoute>} />
      <Route path="/isolation/:pnidNo" element={<PrivateRoute><PnidWorkspaceShell><IsolationPlanningPage /></PnidWorkspaceShell></PrivateRoute>} />
      <Route path="/admin_dashboard" element={<PnidWorkspaceShell><AdminRoute /></PnidWorkspaceShell>} />
      <Route path="/admin" element={<PrivateRoute requireVerified><PnidWorkspaceShell><AdminFormSchemaPage /></PnidWorkspaceShell></PrivateRoute>} />
      <Route path="/policy-admin" element={<PrivateRoute requireVerified><PnidWorkspaceShell><PolicyAdminPage /></PnidWorkspaceShell></PrivateRoute>} />
      <Route path="/pipeline-inspection/:runId" element={<PrivateRoute><PnidWorkspaceShell><PipelineInspectionPage /></PnidWorkspaceShell></PrivateRoute>} />
      <Route path="/work" element={<PackageRoute><PnidWorkspaceShell><PackageListPage /></PnidWorkspaceShell></PackageRoute>} />
      <Route path="/add" element={<PackageRoute><PnidWorkspaceShell><AddDrawingsPage /></PnidWorkspaceShell></PackageRoute>} />
      <Route path="/file-manager" element={<PackageRoute><PnidWorkspaceShell><FileManagerPage /></PnidWorkspaceShell></PackageRoute>} />
      <Route path="/drawings" element={<DrawingsRoute><PnidWorkspaceShell><DrawingsLibraryPage /></PnidWorkspaceShell></DrawingsRoute>} />
      <Route path="/plant-hierarchy" element={<PlantAssetsRoute><PnidWorkspaceShell><PlantHierarchyPage /></PnidWorkspaceShell></PlantAssetsRoute>} />
      {/* Same gate as /plant-hierarchy: the backend reads a plant's legend sheets
          for any signed-in account with a group and takes writes from editors
          only, so the page opens to every scoped account and the editing
          controls gate themselves on can_edit. */}
      <Route path="/legend-sheets" element={<PlantAssetsRoute><PnidWorkspaceShell><LegendSheetsPage /></PnidWorkspaceShell></PlantAssetsRoute>} />
      <Route path="/legend-sheets/:domainId/new" element={<PlantAssetsRoute><PnidWorkspaceShell><NewLegendSheetPage /></PnidWorkspaceShell></PlantAssetsRoute>} />
      <Route path="/legend-sheets/:domainId/:sheetId" element={<PlantAssetsRoute><PnidWorkspaceShell><LegendSheetRecordPage /></PnidWorkspaceShell></PlantAssetsRoute>} />
      <Route path="/packages" element={<Navigate to="/work" replace />} />
      <Route path="/packages/:packageId" element={<PackageRoute><PnidWorkspaceShell><PackageDetailPage /></PnidWorkspaceShell></PackageRoute>} />
      <Route path="/workspace" element={<PackageRoute><PnidWorkspaceShell><WorkspaceLandingPage /></PnidWorkspaceShell></PackageRoute>} />
      <Route path="/workspace/runs/:runId" element={<PackageRoute><PnidWorkspaceShell><WorkspaceRunPage /></PnidWorkspaceShell></PackageRoute>} />
      <Route path="/" element={<HomeRoute />} />
      <Route path="/pnid/:docId" element={<PrivateRoute><PnidWorkspaceShell><App /></PnidWorkspaceShell></PrivateRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
